import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';
import { buildBulkEditApplyRegressionFixtureStyles } from './stylesBulkEditApplyRegressionFixtureData';

/**
 * Fixtures for the DL-66477 epic ("D2 Playwright automation — Styles bulk edit preview, search,
 * the Confirm step, export validation and the activity log", Testmo folder 93165, part 4 of 4).
 * Isolable via `?search=PreviewConfirmLogProbe` (unique substring), with a per-scenario sub-word
 * for further narrowing. Ids in the 96500-96599 range, clear of every other fixture file's ranges
 * (verified by grep — nothing above 95851 exists elsewhere).
 *
 * This epic reuses the baseline "Fabric Notes" CF (id 701, non-mandatory text, already part of
 * `styleCustomFieldDefinitions`) wherever a plain optional custom field is needed, rather than
 * adding a second near-identical one.
 */

const FABRIC_NOTES_CF_ID = 701;
const EXPORT_NOTE_CF_ID = 90;

/** The mandatory-for-export CF layered on top of baseline metadata via `apiTransform` — same
 *  shape/id as the DL-62595 epic's own `mandatoryExportCustomFieldDefinition`, kept local (not
 *  imported) per that file's own convention of each epic owning its transform payload. */
export const exportNoteCustomFieldDefinition = {
  id: EXPORT_NOTE_CF_ID,
  name: 'Export Note',
  position: 8,
  type: 2, // Text
  isMandatory: true,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: 500,
  isMultiLine: true,
};

const fabricNotesEntry = (value: string | null): CustomFieldRowEntry => ({
  id: FABRIC_NOTES_CF_ID,
  type: 'text',
  value: value === null ? null : { id: 0, value },
});

const exportNoteEntry = (value: string | null): CustomFieldRowEntry => ({
  id: EXPORT_NOTE_CF_ID,
  type: 'text',
  value: value === null ? null : { id: 0, value },
});

const base = (
  overrides: Partial<StyleRow> & Pick<StyleRow, 'id' | 'name' | 'customId'>
): StyleRow => ({
  description: null,
  state: 'unpublished',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-02-15T09:00:00',
  readyForExport: false,
  ...overrides,
});

// ── Preview: "Mixed values" correspondence (dl-66478 / Testmo 754722) ────────────────────────
// Two styles sharing the SAME Group (so Group previews the real, common value) but DIFFERING
// Season (so Season previews the "Mixed values" pill) — one selection proves both halves of
// "only where values genuinely differ".
export const previewMixedValuesRowA: StyleRow = base({
  id: 96500,
  name: 'PreviewConfirmLogProbe MixedValues A',
  customId: 'ST-96500',
  season: seasons[0], // AW25
  group: groups[0], // Menswear
  customFields: [fabricNotesEntry('Shared fabric note')],
});
export const previewMixedValuesRowB: StyleRow = base({
  id: 96501,
  name: 'PreviewConfirmLogProbe MixedValues B',
  customId: 'ST-96501',
  season: seasons[2], // SS25 — differs from A
  group: groups[0], // Menswear — matches A
  customFields: [fabricNotesEntry('Shared fabric note')], // matches A too
});

// ── Search box filters both sections (dl-66479 / 754730, dl-66480 / 754731) ──────────────────
// A single 2-style selection is enough — the search box filters the already-rendered field rows,
// it doesn't depend on the styles' own values.
export const searchProbeRowA: StyleRow = base({
  id: 96510,
  name: 'PreviewConfirmLogProbe Search A',
  customId: 'ST-96510',
  customFields: [fabricNotesEntry('Search probe note A')],
});
export const searchProbeRowB: StyleRow = base({
  id: 96511,
  name: 'PreviewConfirmLogProbe Search B',
  customId: 'ST-96511',
  customFields: [fabricNotesEntry('Search probe note B')],
});

// ── Confirm step scope (dl-66481 / 754733, dl-66482 / 754734) ────────────────────────────────
// Three styles sharing a search prefix: A and B are SELECTED (both impacted), C shares the same
// prefix but is left UNSELECTED — its name must never appear in the Confirm step's impacted list,
// proving the list is scoped to the real selection, not every row the search happens to show.
export const confirmScopeRowA: StyleRow = base({
  id: 96520,
  name: 'PreviewConfirmLogProbe ConfirmScope A',
  customId: 'ST-96520',
  group: groups[0], // Menswear
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
});
export const confirmScopeRowB: StyleRow = base({
  id: 96521,
  name: 'PreviewConfirmLogProbe ConfirmScope B',
  customId: 'ST-96521',
  group: groups[0],
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
});
export const confirmScopeRowC: StyleRow = base({
  id: 96522,
  name: 'PreviewConfirmLogProbe ConfirmScope C — never selected',
  customId: 'ST-96522',
  group: groups[0],
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
});

// ── Ready-for-export mandatory validation (dl-66483 / 754728, dl-66484 / 754729) ─────────────
// A Ready-for-export pair whose mandatory "Export Note" starts genuinely EMPTY (no "Set empty" to
// hide — the reactive path, complementary to the DL-62595/DL-66256 epics' preventive-guard
// coverage) alongside a non-mandatory "Fabric Notes" that is ALSO left empty, so 754729 can prove
// the non-mandatory gap never blocks submission on its own.
export const exportValidationRowA: StyleRow = base({
  id: 96530,
  name: 'PreviewConfirmLogProbe ExportValidation A',
  customId: 'ST-96530',
  readyForExport: true,
  customFields: [exportNoteEntry(null), fabricNotesEntry(null)],
});
export const exportValidationRowB: StyleRow = base({
  id: 96531,
  name: 'PreviewConfirmLogProbe ExportValidation B',
  customId: 'ST-96531',
  readyForExport: true,
  customFields: [exportNoteEntry(null), fabricNotesEntry(null)],
});

// ── Activity log cluster (dl-66485 / 754735, dl-66486 / 754736, dl-66487 / 754737) ───────────
// Two styles, each with a DIFFERENT initial "Fabric Notes" value and a DIFFERENT initial Group —
// deliberately different per style so a leaked/shared response between the two per-style fetches
// would be immediately visible. Group carries a real (non-empty) value on both, so it can be
// cleared for 754737.
export const activityLogRowD: StyleRow = base({
  id: 96540,
  name: 'PreviewConfirmLogProbe ActivityLog D',
  customId: 'ST-96540',
  group: groups[0], // Menswear
  customFields: [fabricNotesEntry('Original note D')],
});
export const activityLogRowE: StyleRow = base({
  id: 96541,
  name: 'PreviewConfirmLogProbe ActivityLog E',
  customId: 'ST-96541',
  group: groups[1], // Womenswear
  customFields: [fabricNotesEntry('Original note E')],
});

// A second pair whose "Fabric Notes" starts EMPTY, isolated by a different search token, for the
// "added" form (754736) — kept separate from the "updated" pair above so one test's mutation can
// never contaminate another's baseline read.
export const activityLogAddedRowD: StyleRow = base({
  id: 96542,
  name: 'PreviewConfirmLogProbe ActivityLogAdded D',
  customId: 'ST-96542',
  customFields: [fabricNotesEntry(null)],
});
export const activityLogAddedRowE: StyleRow = base({
  id: 96543,
  name: 'PreviewConfirmLogProbe ActivityLogAdded E',
  customId: 'ST-96543',
  customFields: [fabricNotesEntry(null)],
});

export const buildPreviewConfirmLogFixtureStyles = (): StyleRow[] => [
  previewMixedValuesRowA,
  previewMixedValuesRowB,
  searchProbeRowA,
  searchProbeRowB,
  confirmScopeRowA,
  confirmScopeRowB,
  confirmScopeRowC,
  exportValidationRowA,
  exportValidationRowB,
  activityLogRowD,
  activityLogRowE,
  activityLogAddedRowD,
  activityLogAddedRowE,
  // DL-66548 epic (Testmo folder 93166 part 5) apply / sort-filter-reapply / regression /
  // activity-log fixtures (id band 97300-97399) are co-registered here rather than adding a
  // separate import + spread to `styles.ts`, which is at its 800-line lint cap. See
  // `stylesBulkEditApplyRegressionFixtureData.ts` for the rows themselves.
  ...buildBulkEditApplyRegressionFixtureStyles(),
];
